/**
 * client/render/fxmath.ts — pure timing helpers for entity fx (unit tested). OWNER: RENDER agent.
 */
import { BOLT_BEEP_DIV, BOLT_BEEP_MIN_MS, BOLT_BEEP_START_MS } from '@shared/constants';

/** explosive_bolt_mp fuseTime 1 s [WPN §2.2]: the bolt sticks (and starts beeping) 1000 ms before it explodes. */
export const BOLT_FUSE_MS = 1000;
/** How long one blink of fx_trail_crossbow_blink_*_os stays lit. ESTIMATE. */
export const BOLT_BLINK_MS = 60;

/**
 * Beep/blink times of a stuck explosive bolt [WPN §2.2, _explosive_bolt.csc fx_think]: the first at the stick
 * time (explodeAt - fuse), then the interval starts at 300 ms and is divided by 1.2 after each beep, clamped to
 * [80, 300] ms (BOLT_BEEP_*). Returns server-time ms of every beep before explodeAt.
 */
export function boltBeepTimes(explodeAt: number, fuseMs = BOLT_FUSE_MS): number[] {
  const out: number[] = [];
  let t = explodeAt - fuseMs;
  let interval = BOLT_BEEP_START_MS;
  while (t < explodeAt && out.length < 64) {
    out.push(t);
    t += interval;
    interval = Math.max(BOLT_BEEP_MIN_MS, Math.min(BOLT_BEEP_START_MS, interval / BOLT_BEEP_DIV));
  }
  return out;
}

/** Index of the last beep at or before `now` (-1 = none yet). */
export function boltBeepIndex(now: number, explodeAt: number, fuseMs = BOLT_FUSE_MS): number {
  const ts = boltBeepTimes(explodeAt, fuseMs);
  let k = -1;
  for (let i = 0; i < ts.length; i++) if (ts[i] <= now) k = i;
  return k;
}

/** Is the blink light on at `now`? */
export function boltBlinkOn(now: number, explodeAt: number, fuseMs = BOLT_FUSE_MS): boolean {
  if (explodeAt <= 0 || now >= explodeAt) return false;
  const ts = boltBeepTimes(explodeAt, fuseMs);
  for (const t of ts) if (now >= t && now < t + BOLT_BLINK_MS) return true;
  return false;
}

/** Spin angle (deg) of a flying hatchet: "Rotate one axis, blade style" (hatchet_mp rotate 1). Rate ESTIMATE. */
export const HATCHET_SPIN_DEG_PER_S = 900;
export function hatchetSpin(ageMs: number): number {
  return ((ageMs / 1000) * HATCHET_SPIN_DEG_PER_S) % 360;
}
